/**
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 **/

import React, { useCallback, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Namespaces } from '../../../i18n/i18n';
import { Devices, Os, AndroidVersionNames } from '../../../utils/constants';
import { OsRestrictions } from '../../../types';

const { Mobile, Tablet } = Devices;
const { Android, Ios } = Os;

type RestrictionCardMobileProps = {
  device: typeof Mobile | typeof Tablet,
  className?: string,
  os: OsRestrictions[],
};

const defaultProps = {
  className: '',
};

const RestrictionCardMobile: React.FC<RestrictionCardMobileProps> = ({ device, os, className }: RestrictionCardMobileProps): ReactElement => {
  const { t } = useTranslation(Namespaces.Common);

  const getLabelText = useCallback((osName: string): string => {
    let labelKey: string = 'Android';
    if (osName === Ios) {
      labelKey = device === Tablet ? 'iPad' : 'iPhone';
    }
    return t(`restrictions.devices.${device}.${labelKey}.title`);
  }, [device, t]);

  const getVersionText = useCallback((osName: string, minVersion: string): string => {
    const [majorVersion] = minVersion.split('.');
    if (osName === Android) {
      const version = AndroidVersionNames.find(
        (androidVersion: { number: string, name: string }) => androidVersion.number === majorVersion
      );
      return version ? `${version.name} ${version.number}+` : `${majorVersion}+`;
    }
    return `${majorVersion}+`;
  }, []);

  const beautifyName = useCallback((name: string) => {
    switch (name) {
      case Ios: return 'iOS';
      case Android: return 'Android';
      default: return name;
    }
  }, []);

  return (
    <div className={className}>
      {os.map(({ name, minVersion }) => (
        <div key={`os-${name}`}>
          <h5 className="device-name">{getLabelText(name)}</h5>
          <ul className="os-browser-versions">
            <li>
              <span>{t(`${beautifyName(name)}`)}</span>
              {' '}
              {minVersion === '' ? null : getVersionText(name, minVersion)}
            </li>
          </ul>
        </div>
      ))}
    </div>
  );
};

RestrictionCardMobile.defaultProps = defaultProps;

export default RestrictionCardMobile;
